import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

function formatWhen(value?: string | null): string {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return value
  return d.toLocaleString()
}

export type InboxItem = {
  id: number
  title: string
  body: string
  ctaLabel?: string
  ctaPath?: string
  read: boolean
  createdAt?: string
}

type Props = {
  load: () => Promise<{ unreadCount: number; notifications: InboxItem[] }>
  markRead: (id?: number) => Promise<{ unreadCount: number }>
  classPrefix?: 'cp' | 'ap'
  pollMs?: number
}

export function InboxBell({ load, markRead, classPrefix = 'cp', pollMs = 45000 }: Props) {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<InboxItem[]>([])
  const [unread, setUnread] = useState(0)
  const root = useRef<HTMLDivElement>(null)
  const loadRef = useRef(load)
  const markReadRef = useRef(markRead)

  useEffect(() => {
    loadRef.current = load
  }, [load])

  useEffect(() => {
    markReadRef.current = markRead
  }, [markRead])

  const refresh = useCallback(async () => {
    try {
      const res = await loadRef.current()
      setItems(res.notifications || [])
      setUnread(res.unreadCount || 0)
    } catch {
      /* best-effort */
    }
  }, [])

  useEffect(() => {
    void refresh()
    const id = window.setInterval(() => {
      void refresh()
    }, pollMs)
    return () => window.clearInterval(id)
  }, [refresh, pollMs])

  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  async function onOpen() {
    const next = !open
    setOpen(next)
    if (next) await refresh()
  }

  async function onMarkAll() {
    try {
      const res = await markReadRef.current()
      setUnread(res.unreadCount || 0)
      setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    } catch {
      /* ignore */
    }
  }

  async function onMarkOne(id: number) {
    try {
      const res = await markReadRef.current(id)
      setUnread(res.unreadCount || 0)
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)))
    } catch {
      /* ignore */
    }
  }

  function ctaNode(n: InboxItem) {
    const path = (n.ctaPath || '').trim()
    const label = n.ctaLabel || 'Open'
    if (!path) return null
    if (path.startsWith('/')) {
      return (
        <Link
          to={path}
          onClick={(e) => {
            e.stopPropagation()
            void onMarkOne(n.id)
            setOpen(false)
          }}
        >
          {label}
        </Link>
      )
    }
    if (path.startsWith('https://') || path.startsWith('http://')) {
      return (
        <a
          href={path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.stopPropagation()
            void onMarkOne(n.id)
          }}
        >
          {label}
        </a>
      )
    }
    return null
  }

  const wrap = `${classPrefix}-notify`
  const btn = `${classPrefix}-topbar__notify`
  const panel = `${classPrefix}-notify__panel`

  return (
    <div className={wrap} ref={root}>
      <button
        type="button"
        className={btn}
        aria-label={unread ? `${unread} unread notifications` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => void onOpen()}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.3 21a1.9 1.9 0 0 0 3.4 0"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {unread > 0 ? <span className={`${wrap}__badge`}>{unread > 9 ? '9+' : unread}</span> : null}
      </button>
      {open ? (
        <div className={panel} role="dialog" aria-label="Notifications">
          <div className={`${wrap}__head`}>
            <strong>Notifications</strong>
            <span className={`${wrap}__head-actions`}>
              {unread > 0 ? (
                <button type="button" className={`${wrap}__refresh`} onClick={() => void onMarkAll()}>
                  Mark all read
                </button>
              ) : null}
              <button type="button" className={`${wrap}__refresh`} onClick={() => void refresh()}>
                Refresh
              </button>
            </span>
          </div>
          {items.length === 0 ? (
            <p className={`${wrap}__empty`}>No new notifications.</p>
          ) : (
            <ul className={`${wrap}__list`}>
              {items.map((n) => (
                <li
                  key={n.id}
                  className={`${wrap}__item${n.read ? '' : ' is-unread'}`}
                  onClick={() => {
                    if (!n.read) void onMarkOne(n.id)
                  }}
                >
                  <strong>{n.title}</strong>
                  <p>{n.body}</p>
                  {ctaNode(n)}
                  {n.createdAt ? <time dateTime={n.createdAt}>{formatWhen(n.createdAt)}</time> : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  )
}
